/**
 * Guest-first access — authenticated server entry points for the guest
 * handoff and claim.
 *
 * Neither identity is ever accepted from the caller as authority:
 * `requireSupabaseAuth` verifies the bearer and puts `userId` and `claims` on
 * the context; the GUEST side of a claim is verified by the adapter from a
 * server-issued ticket. The privileged adapter is imported INSIDE each handler
 * so the service-role client never enters a client bundle.
 */

import { createServerFn } from "@tanstack/react-start";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  beginGuestHandoffCommand,
  claimGuestHistoryCommand,
  type GuestClaimOutcome,
  type GuestHandoffOutcome,
} from "./guestClaim";
import { validateGuestClaimInput } from "./guestRequest";

const actorOf = (context: { userId: string; claims: unknown }) => ({
  userId: context.userId,
  isAnonymous: (context.claims as { is_anonymous?: unknown }).is_anonymous === true,
});

/** Called by the GUEST, right before it starts signing in with another identity. */
export const beginGuestHandoff = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<GuestHandoffOutcome> => {
    const { supabaseGuestClaimPort } = await import("./guestClaimAdapter.server");
    return beginGuestHandoffCommand(supabaseGuestClaimPort, actorOf(context));
  });

/** Called by the ACCOUNT, once signed in, to bring the guest's history along. */
export const claimGuestHistory = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(validateGuestClaimInput)
  .handler(async ({ data, context }): Promise<GuestClaimOutcome> => {
    const { supabaseGuestClaimPort } = await import("./guestClaimAdapter.server");
    return claimGuestHistoryCommand(supabaseGuestClaimPort, actorOf(context), data);
  });
